import { FC, useState, useEffect, useCallback } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import { useAtom } from 'jotai';
import request from 'lib/request';
import { PageHeader } from 'components/page-header';
import { queueSummaryAtom } from './atoms';
import type { QueueBreakdown, BreakdownEntry } from './types';
import { formatNumber } from 'utils/number';
import { AutoRefreshControls } from 'pages/admin/auto-refresh-controls';
import { ScanDepthDropdown } from 'pages/admin/scan-depth-dropdown';
import { ScanNote } from 'pages/admin/scan-note';
import {
  DEFAULT_POLLING_INTERVAL,
  DEFAULT_SCAN_DEPTH,
} from 'pages/admin/utils';
import type { ScanDepth } from 'pages/admin/utils';
import styles from './page.module.scss';

const format0 = (n: number): string => formatNumber(n, 0);

const BreakdownTable: FC<{
  title: string;
  description?: string;
  entries: BreakdownEntry[];
}> = ({ title, description, entries }) => {
  const maxPct =
    entries.length > 0 ? Math.max(...entries.map((e) => e.percentage)) : 0;

  return (
    <div className={styles.breakdownSection}>
      <h4 className={styles.breakdownTitle}>{title}</h4>
      {description && (
        <p className={styles.breakdownDescription}>{description}</p>
      )}
      <table className={styles.breakdownTable}>
        <thead>
          <tr>
            <th>Type</th>
            <th className={styles.countCell}>Count</th>
            <th className={styles.pctCell}>%</th>
            <th className={styles.barCell}></th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={4} className={styles.emptyRow}>
                Queue is empty
              </td>
            </tr>
          ) : (
            entries.map((entry) => (
              <tr key={entry.name}>
                <td>{entry.name}</td>
                <td className={styles.countCell}>{format0(entry.count)}</td>
                <td className={styles.pctCell}>{entry.percentage}%</td>
                <td className={styles.barCell}>
                  <div
                    className={styles.bar}
                    style={{
                      width: `${
                        maxPct > 0 ? (entry.percentage / maxPct) * 100 : 0
                      }%`,
                    }}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export const IntersectionCoordinationQueuePage: FC = () => {
  const [summaryState, refetchSummary] = useAtom(queueSummaryAtom);
  const [breakdown, setBreakdown] = useState<QueueBreakdown | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState(
    DEFAULT_POLLING_INTERVAL
  );
  const [scanDepth, setScanDepth] = useState<ScanDepth>(DEFAULT_SCAN_DEPTH);

  useEffect(() => {
    if (pollingInterval === 0) return;
    const interval = setInterval(() => {
      refetchSummary();
    }, pollingInterval);
    return () => clearInterval(interval);
  }, [refetchSummary, pollingInterval]);

  const handleScanQueue = useCallback(async () => {
    setBreakdownLoading(true);
    setBreakdownError(null);
    try {
      const response = await request.get<QueueBreakdown>(
        `/admin/intersection_coordination_queue/breakdown?sample_limit=${scanDepth}`
      );
      if ('error' in response) throw new Error(response.error);
      setBreakdown(response);
    } catch (err) {
      setBreakdownError(
        err instanceof Error ? err.message : 'Failed to scan queue'
      );
    } finally {
      setBreakdownLoading(false);
    }
  }, [scanDepth]);

  const renderSummary = () => {
    if (summaryState.state === 'loading') {
      return <InlineLoading description="Loading queue stats..." />;
    }

    if (summaryState.state === 'hasError') {
      return (
        <div className={styles.error}>
          Error loading queue stats:{' '}
          {summaryState.error instanceof Error
            ? summaryState.error.message
            : 'Unknown error'}
        </div>
      );
    }

    const data = summaryState.data;

    return (
      <>
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Queue Depth</div>
            <div className={styles.summaryValue}>{format0(data.queueSize)}</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Latency</div>
            <div className={styles.summaryValue}>
              {data.latencySeconds}
              <span className={styles.summaryUnit}>sec</span>
            </div>
            <div className={styles.summaryHint}>
              Age of the oldest job in the queue (time since it was enqueued). A
              high value means jobs are waiting longer than usual to be
              processed.
            </div>
          </div>
        </div>
        <AutoRefreshControls
          generatedAt={data.generatedAt}
          onRefresh={() => refetchSummary()}
          pollingInterval={pollingInterval}
          onPollingIntervalChange={setPollingInterval}
        />
      </>
    );
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Intersection Coordination Queue"
        actions={null}
      />
      <div className={styles.pageContent}>
        <p className={styles.description}>
          Monitor the intersection_coordination Sidekiq queue. Use &quot;Scan
          Queue&quot; to get a breakdown of job types (scans first 5,000 jobs).
        </p>

        {renderSummary()}

        <div className={styles.scanSection}>
          <Button
            kind="secondary"
            size="sm"
            onClick={handleScanQueue}
            disabled={breakdownLoading}
          >
            {breakdownLoading ? 'Scanning...' : 'Scan Queue'}
          </Button>
          <ScanDepthDropdown
            id="scan-depth-dropdown"
            value={scanDepth}
            onChange={setScanDepth}
            disabled={breakdownLoading}
          />
          {breakdownLoading && (
            <InlineLoading description="Scanning queue jobs..." />
          )}
        </div>

        {breakdownError && <div className={styles.error}>{breakdownError}</div>}

        {/* Always rendered, so a screen reader hears each new result note. */}
        <div aria-live="polite" data-testid="ic-scan-note">
          {breakdown && (
            <ScanNote
              totals={breakdown}
              label="intersection_coordination"
              options={{ newestFirst: true }}
            />
          )}
        </div>

        {breakdown && (
          <>
            <BreakdownTable
              title="By Source Type"
              description="What model is generating the work? Helps identify if a particular record type is flooding the queue (e.g., a bulk subtract import or a snapshot ingest creating many slot callbacks)."
              entries={breakdown.bySourceType}
            />
            <BreakdownTable
              title="By Workflow Type"
              description='What kind of computation is being requested? Helps identify which workflow is dominating (e.g., NRI recalcs vs geometry recalculations). "source_class_routed" means no explicit workflow was set — the job routes based on the source model class.'
              entries={breakdown.byWorkflowType}
            />
          </>
        )}
      </div>
    </>
  );
};
